import React from 'react';
import { Info, Clock, Globe, MapPin, Calendar, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о часовых поясах, их использовании и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о часовых поясах: устройство, история, использование
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-purple-500" />
          Что такое часовой пояс?
        </h3>
        <p>
          <strong>Часовой пояс</strong> — это регион на Земле, в котором установлено определённое стандартное время, обычно называемое поясным временем. Земной шар разделён на 24 часовых пояса, приблизительно по 15° долготы каждый. Соседние часовые пояса обычно отличаются на один час, однако на практике границы поясов могут быть значительно искривлены в соответствии с государственными, административными и экономическими границами.
        </p>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-purple-500" />
          Координированное всемирное время (UTC)
        </h3>
        <p>
          Современный мир использует систему <strong>Координированного всемирного времени (UTC)</strong> в качестве основы для определения часовых поясов. UTC — это эталонное время, относительно которого указываются смещения для всех часовых поясов в мире. Например, московское время обозначается как UTC+3, что означает, что в Москве время на 3 часа опережает UTC.
        </p>
        <p>
          Ранее использовалось гринвичское среднее время (GMT), однако UTC основано на атомных часах и обеспечивает более высокую точность измерения.
        </p>

        <h3 className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-500" />
          Часовые пояса России
        </h3>
        <p>
          Россия — самая большая страна мира по территории, она расположена в 11 часовых поясах, что больше, чем у любой другой страны. С запада на восток:
        </p>
        <ul>
          <li><strong>UTC+2</strong> — Калининградская область</li>
          <li><strong>UTC+3</strong> — Москва, Санкт-Петербург и большая часть европейской России</li>
          <li><strong>UTC+4</strong> — Самара, Удмуртия, Астрахань</li>
          <li><strong>UTC+5</strong> — Екатеринбург и регионы Урала</li>
          <li><strong>UTC+6</strong> — Омск</li>
          <li><strong>UTC+7</strong> — Новосибирск, Кемерово, Красноярск</li>
          <li><strong>UTC+8</strong> — Иркутск, Улан-Удэ</li>
          <li><strong>UTC+9</strong> — Якутск, Чита</li>
          <li><strong>UTC+10</strong> — Хабаровск, Владивосток</li>
          <li><strong>UTC+11</strong> — Магадан, Сахалин</li>
          <li><strong>UTC+12</strong> — Камчатка, Чукотка</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Интересный факт</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            До 2011 года в России действовало сезонное изменение времени (переход на "летнее" и "зимнее" время). В 2011 году Россия отказалась от сезонного перехода времени и оставила постоянное "летнее" время, а в 2014 году перешла на постоянное "зимнее" время, увеличив количество часовых поясов с 9 до 11.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-green-500" />
          Международная линия перемены дат
        </h3>
        <p>
          Международная линия перемены дат — это воображаемая линия на поверхности Земли, проходящая в основном по 180-му меридиану. При пересечении этой линии с запада на восток календарная дата меняется на один день назад, а при пересечении с востока на запад — на один день вперёд.
        </p>
        <p>
          Эта линия проходит через Тихий океан и зигзагообразно огибает некоторые острова и территории для удобства местных жителей. Например, все острова государства Кирибати находятся по одну сторону от линии перемены дат, хотя географически они расположены по обе стороны от 180-го меридиана.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Джетлаг и как с ним справиться
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-2">
            <strong>Джетлаг</strong> (синдром смены часовых поясов) — это физиологическое состояние, вызванное нарушением циркадных ритмов из-за быстрого перемещения через несколько часовых поясов, обычно при авиаперелётах.
          </p>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Для минимизации эффекта джетлага до поездки начните постепенно смещать время сна в сторону целевого часового пояса</li>
            <li>Во время полета на восток старайтесь спать в самолете; на запад - постарайтесь не спать до ночи по местному времени</li>
            <li>По прибытии важно выйти на солнечный свет, который помогает перестроить биологические часы</li>
            <li>Избегайте кофеина и алкоголя, которые могут усугубить симптомы</li>
            <li>Придерживайтесь местного времени приема пищи и режима дня сразу после прибытия</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Практические советы по работе с часовыми поясами
        </h3>
        <ul>
          <li><strong>Планирование международных звонков и встреч</strong> — всегда указывайте часовой пояс при назначении времени встречи, например: «15:00 по московскому времени (MSK)» или используйте UTC: «12:00 UTC».</li>
          <li><strong>Используйте цифровые инструменты</strong> — современные календари и планировщики встреч автоматически учитывают разницу часовых поясов для каждого участника.</li>
          <li><strong>Учитывайте рабочее время</strong> — при планировании международных коммуникаций учитывайте не только разницу во времени, но и типичное рабочее время в целевой стране.</li>
          <li><strong>Путешествия по России</strong> — при поездках между регионами России обязательно проверяйте местное время, особенно при перемещениях между Европейской частью и Дальним Востоком, где разница может достигать 9 часов.</li>
          <li><strong>Обновляйте устройства</strong> — смартфоны и компьютеры обычно автоматически обновляют время при смене часового пояса, но это требует подключения к интернету.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-500" />
          Обозначения часовых поясов
        </h3>
        <p>
          Существует несколько систем обозначения часовых поясов:
        </p>
        <ul>
          <li><strong>Смещение относительно UTC</strong> — наиболее распространённый способ, например: UTC+3, UTC-5 и т.д.</li>
          <li><strong>Военное время или ZULU</strong> — используется в авиации и военном деле. Каждому часовому поясу присвоена буква от A до Z (кроме J). Время UTC обозначается как "ZULU time" или "Z".</li>
          <li><strong>IANA (Olson)</strong> — база данных часовых поясов, используемая в компьютерных системах. Использует обозначения вида "Регион/Город", например: "Europe/Moscow", "America/New_York".</li>
          <li><strong>Локальные сокращения</strong> — такие как MSK (московское время), EST (восточное стандартное время США) и т.д.</li>
        </ul>

        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800 my-6">
          <h4 className="text-purple-700 dark:text-purple-300 font-medium">Наш конвертер</h4>
          <p className="text-purple-600 dark:text-purple-400 text-sm mb-0">
            Наш конвертер часовых поясов позволяет мгновенно узнать текущее время в любом городе мира, рассчитать разницу во времени между городами и спланировать международные встречи с учетом часовых поясов всех участников. Просто выберите нужные города и получите точное время и рекомендации!
          </p>
        </div>
      </div>
    </article>
  );
};

export default InfoContent;